/**
 * AgentOsAgencyLandingPage.jsx
 * ------------------------------------------------------------------
 * Funnel A — Agency Owner landing page (cold traffic destination).
 *
 * Štýl: inšpirované "Profitron Bot" patternom — tmavá farba, gold accent,
 * emocionálny SK coaching tón s tykaním, punchy CTA. Ale predáva REAL
 * B2B SaaS hodnotu (white-label AI agentov pre agentúry).
 *
 * Sekcie:
 *   Hero (full-bleed dark, gold accents) → Pain points (3 cards) →
 *   The shift (before/after) → Agent portfolio (preview 5+15) →
 *   ROI Calculator → Social proof → 3-tier pricing → FAQ → Lead form
 *
 * Lead capture: POST /api/agent-os/leads/agency → enrolls into
 * `agent_os_agency` drip sequence in funnel_emails.py.
 */
import { useState, useMemo } from "react";
import axios from "axios";
import { API } from "../config";
import {
  Bot, ArrowRight, CheckCircle, AlertTriangle, Zap, Globe2,
  TrendingUp, Loader2, Sparkles, Lock, Users, Layers, Crown,
} from "lucide-react";
import {
  AgentOsExitPopup, LiveLeadCounter, CalendlySuccessEmbed, AgentOsPixelTracker,
} from "../components/agent-os/AgentOsShared";

const AGENT_PREVIEW = [
  { icon: "💬", name: "Helpdesk Agent",     desc: "Odpovedá zákazníkom 24/7 v 26 jazykoch" },
  { icon: "⭐", name: "Review Reply Agent", desc: "Google + Heureka + Trustpilot odpovede" },
  { icon: "🛒", name: "Cart Recovery",      desc: "AI-personalized email + SMS recovery" },
  { icon: "✍️", name: "Product Desc",       desc: "SEO + GEO optimized v 26 jazykoch" },
  { icon: "📧", name: "Newsletter",         desc: "Full-auto týždenný newsletter z katalógu" },
];

export default function AgentOsAgencyLandingPage() {
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white selection:bg-yellow-500 selection:text-black" data-testid="agent-os-agency-landing">
      <AgentOsPixelTracker event="ViewContent" />
      <AgentOsExitPopup variant="agency" />
      <TopBar />
      <Hero />
      <PainSection />
      <ShiftSection />
      <AgentPreviewSection />
      <CalculatorSection />
      <SocialProofSection />
      <PricingSection />
      <FaqSection />
      <LeadFormSection />
      <Footer />
    </div>
  );
}

function TopBar() {
  return (
    <div className="border-b border-yellow-900/30 bg-black/80 backdrop-blur-xl sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
        <a href="/" className="flex items-center gap-2" data-testid="agent-os-logo">
          <div className="w-8 h-8 bg-gradient-to-br from-yellow-400 to-yellow-600 flex items-center justify-center">
            <Bot className="w-5 h-5 text-black" />
          </div>
          <span className="font-black tracking-tight">
            TopRankPulse <span className="text-yellow-400">· Agent OS</span>
          </span>
        </a>
        <a
          href="#pricing"
          data-testid="agent-os-topbar-cta"
          className="px-4 py-2 bg-yellow-400 hover:bg-yellow-300 text-black text-xs font-black uppercase tracking-widest"
        >
          Aktivovať
        </a>
      </div>
    </div>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden" data-testid="agent-os-hero">
      {/* Atmospheric gradient blobs */}
      <div className="absolute -top-32 -left-20 w-[500px] h-[500px] bg-yellow-500/10 blur-[120px] pointer-events-none" />
      <div className="absolute top-40 right-0 w-[400px] h-[400px] bg-purple-600/10 blur-[100px] pointer-events-none" />

      <div className="relative max-w-6xl mx-auto px-6 pt-20 pb-24">
        <div className="inline-flex items-center gap-2 px-3 py-1 border border-yellow-700/40 bg-yellow-900/10 mb-8" data-testid="agent-os-hero-badge">
          <Sparkles className="w-3 h-3 text-yellow-400" />
          <span className="text-[10px] uppercase tracking-[0.25em] text-yellow-300 font-bold">
            Beta · Lock-in cena 99 €/mes navždy
          </span>
        </div>

        <div className="mb-6">
          <LiveLeadCounter variant="agency" />
        </div>

        <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black leading-[1.05] mb-6 max-w-4xl">
          Najmi si agentúru,
          <br />
          ktorá <span className="text-yellow-400">nespí, neje</span>
          <br />
          a nepýta zvýšenie. 💼
        </h1>

        <p className="text-lg sm:text-xl text-gray-400 max-w-2xl mb-3 leading-relaxed">
          Tvoj najlepší account manager práve dostal ponuku z konkurencie.
          O 2 mesiace odchádza. 6 klientov zostáva bez tváre.
        </p>
        <p className="text-lg sm:text-xl text-white max-w-2xl mb-10 leading-relaxed font-bold">
          AI Agent nikdy neodíde. Pracuje 24/7. Hovorí 26 jazykov.
        </p>

        <div className="flex flex-wrap gap-4 mb-12">
          <a
            href="#lead-form"
            data-testid="agent-os-hero-primary-cta"
            className="inline-flex items-center gap-2 px-7 py-4 bg-yellow-400 hover:bg-yellow-300 text-black font-black uppercase tracking-wider text-sm transition-all shadow-[0_0_40px_rgba(250,204,21,0.3)]"
          >
            Pozri demo (15 min) <ArrowRight className="w-4 h-4" />
          </a>
          <a
            href="#calculator"
            data-testid="agent-os-hero-secondary-cta"
            className="inline-flex items-center gap-2 px-7 py-4 border border-gray-700 hover:border-yellow-400 text-white font-bold uppercase tracking-wider text-sm transition-colors"
          >
            Spočítaj koľko zarobíš
          </a>
        </div>

        <div className="flex flex-wrap gap-8 text-xs text-gray-500 font-mono uppercase tracking-wider">
          <span><span className="text-yellow-400 font-black">47</span> agentúr · beta</span>
          <span><span className="text-yellow-400 font-black">20</span> AI agentov</span>
          <span><span className="text-yellow-400 font-black">26</span> jazykov</span>
          <span><span className="text-yellow-400 font-black">70%</span> margin</span>
        </div>
      </div>
    </section>
  );
}

function PainSection() {
  const pains = [
    {
      icon: <Users className="w-8 h-8 text-rose-400" />,
      title: "Junior chce odísť",
      body: "Trénoval si ho 4 mesiace. Teraz dostal ponuku +500 € z väčšej agentúry. Akciá: hľadať náhradu 2 mesiace, onboarding 3 mesiace, modliť sa že to vydrží.",
    },
    {
      icon: <Globe2 className="w-8 h-8 text-rose-400" />,
      title: "Klient chce DE/AT/PL trh",
      body: "Tvoja agentúra hovorí slovensky. Klient chce expanziu. Buď najmeš nemeckého copywritera (€2500/mes), alebo strácaš klienta.",
    },
    {
      icon: <AlertTriangle className="w-8 h-8 text-rose-400" />,
      title: "Stropuješ na cene",
      body: "Klient platí €1500/mes retainer. Nemôžeš ísť vyššie — nie sú výsledky úmerné. Ale máš fixné náklady = ľudí. Margin tenká ako papier.",
    },
  ];
  return (
    <section className="border-y border-gray-900 bg-black py-20 px-6" data-testid="agent-os-pain">
      <div className="max-w-6xl mx-auto">
        <div className="text-xs uppercase tracking-[0.3em] text-rose-400 font-bold mb-3">
          Realita
        </div>
        <h2 className="text-3xl sm:text-4xl font-black mb-12 max-w-2xl">
          Toto sa stane do <span className="text-rose-400">6 mesiacov</span>, ak nič nezmeníš.
        </h2>
        <div className="grid md:grid-cols-3 gap-6">
          {pains.map((p, i) => (
            <div
              key={i}
              data-testid={`agent-os-pain-${i}`}
              className="p-6 bg-gradient-to-br from-rose-950/20 to-black border border-rose-900/30 hover:border-rose-700/60 transition-colors"
            >
              <div className="mb-4">{p.icon}</div>
              <h3 className="text-xl font-black mb-2">{p.title}</h3>
              <p className="text-sm text-gray-400 leading-relaxed">{p.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ShiftSection() {
  return (
    <section className="py-24 px-6 bg-gradient-to-b from-black to-[#0a0a0a]" data-testid="agent-os-shift">
      <div className="max-w-6xl mx-auto">
        <div className="text-xs uppercase tracking-[0.3em] text-yellow-400 font-bold mb-3">
          Posun
        </div>
        <h2 className="text-3xl sm:text-5xl font-black mb-4 max-w-3xl">
          Predaj <span className="text-yellow-400">agentov</span> namiesto hodín.
        </h2>
        <p className="text-lg text-gray-400 max-w-2xl mb-16">
          Tvoja agentúra prestane účtovať za čas ľudí. Začne predávať <strong className="text-white">výsledky</strong>, ktoré dodá AI. Lepšie pre teba, lacnejšie pre klienta.
        </p>

        <div className="grid md:grid-cols-2 gap-10">
          {/* PRED */}
          <div className="p-8 bg-black border border-gray-800">
            <div className="text-xs uppercase tracking-[0.3em] text-gray-500 mb-3">Pred</div>
            <h3 className="text-2xl font-black mb-6 text-gray-400">Klasická agentúra</h3>
            <ul className="space-y-3 text-sm text-gray-400">
              <li className="flex gap-3"><span className="text-rose-400">✗</span> 6 ľudí × €1 800/mes = €10 800 fixné náklady</li>
              <li className="flex gap-3"><span className="text-rose-400">✗</span> Práca len 8h/deň, len v pracovné dni</li>
              <li className="flex gap-3"><span className="text-rose-400">✗</span> 1 jazyk = 1 trh</li>
              <li className="flex gap-3"><span className="text-rose-400">✗</span> Churn ľudí 30 %/rok</li>
              <li className="flex gap-3"><span className="text-rose-400">✗</span> Margin 15-25 %</li>
            </ul>
          </div>
          {/* PO */}
          <div className="p-8 bg-gradient-to-br from-yellow-950/30 to-black border-2 border-yellow-700/60 relative">
            <div className="absolute -top-3 -right-3 px-3 py-1 bg-yellow-400 text-black text-[10px] font-black uppercase tracking-widest">
              Toprankpulse
            </div>
            <div className="text-xs uppercase tracking-[0.3em] text-yellow-400 mb-3">Po</div>
            <h3 className="text-2xl font-black mb-6 text-white">Agency + Agent OS</h3>
            <ul className="space-y-3 text-sm text-gray-300">
              <li className="flex gap-3"><span className="text-yellow-400">✓</span> 1 osoba + 20 AI agentov = €399/mes náklady</li>
              <li className="flex gap-3"><span className="text-yellow-400">✓</span> 24/7/365 nonstop práca</li>
              <li className="flex gap-3"><span className="text-yellow-400">✓</span> 26 jazykov, DE/AT/PL trhy automaticky</li>
              <li className="flex gap-3"><span className="text-yellow-400">✓</span> 0 % churn (agent neodíde)</li>
              <li className="flex gap-3"><span className="text-yellow-400">✓</span> Margin <strong>70 %</strong> per klient</li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function AgentPreviewSection() {
  return (
    <section className="py-24 px-6 bg-[#0a0a0a]" data-testid="agent-os-preview">
      <div className="max-w-6xl mx-auto">
        <div className="text-xs uppercase tracking-[0.3em] text-yellow-400 font-bold mb-3">
          Tvoja AI armáda
        </div>
        <h2 className="text-3xl sm:text-5xl font-black mb-4">
          20 agentov. Každý jeden ušetrí <span className="text-yellow-400">€1 500+</span>/mes.
        </h2>
        <p className="text-lg text-gray-400 max-w-2xl mb-12">
          Tu je 5 najobľúbenejších. Ďalších 15 odomkneš v dashboarde.
        </p>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
          {AGENT_PREVIEW.map((a, i) => (
            <div
              key={a.name}
              data-testid={`agent-os-preview-${i}`}
              className="p-5 bg-black border border-gray-800 hover:border-yellow-700/60 transition-all hover:translate-y-[-2px]"
            >
              <div className="text-3xl mb-3">{a.icon}</div>
              <h3 className="text-base font-black mb-1">{a.name}</h3>
              <p className="text-sm text-gray-400">{a.desc}</p>
            </div>
          ))}
          <div className="p-5 bg-gradient-to-br from-yellow-950/30 to-black border border-yellow-700/40 flex flex-col justify-center items-center text-center">
            <Layers className="w-8 h-8 text-yellow-400 mb-3" />
            <p className="text-sm font-bold text-yellow-300">+ 15 ďalších agentov</p>
            <p className="text-xs text-gray-400 mt-1">Helpdesk, Translator, Fraud, Pricing, Inventory…</p>
          </div>
        </div>

        <div className="text-center">
          <a
            href="/agent-os/demo/review-reply"
            data-testid="agent-os-agency-demo-link"
            className="inline-flex items-center gap-2 px-6 py-3 border-2 border-yellow-700/60 hover:border-yellow-400 bg-black/40 text-yellow-300 hover:text-white font-bold uppercase tracking-widest text-xs transition-colors"
          >
            🎬 Vyskúšaj Review Agenta naživo (bez registrácie) <ArrowRight className="w-3 h-3" />
          </a>
        </div>
      </div>
    </section>
  );
}

function CalculatorSection() {
  const [clients, setClients] = useState(20);
  const [pricePerAgent, setPricePerAgent] = useState(150);
  const [agentsPerClient, setAgentsPerClient] = useState(3);

  const { revenue, ourCost, margin, marginPct, vsHire } = useMemo(() => {
    const revenue = clients * agentsPerClient * pricePerAgent;
    const ourCost = 399; // Pro tier
    const margin = revenue - ourCost;
    const marginPct = revenue > 0 ? Math.round((margin / revenue) * 100) : 0;
    const vsHire = (agentsPerClient * 1800 * clients) - revenue;
    return { revenue, ourCost, margin, marginPct, vsHire };
  }, [clients, pricePerAgent, agentsPerClient]);

  return (
    <section id="calculator" className="py-24 px-6 bg-black border-y border-yellow-900/30" data-testid="agent-os-calculator">
      <div className="max-w-5xl mx-auto">
        <div className="text-xs uppercase tracking-[0.3em] text-yellow-400 font-bold mb-3">
          ROI Kalkulačka
        </div>
        <h2 className="text-3xl sm:text-5xl font-black mb-4">
          Koľko zarobíš <span className="text-yellow-400">navyše</span> každý mesiac?
        </h2>
        <p className="text-lg text-gray-400 mb-12 max-w-2xl">
          Posuň posuvníky podľa svojej agentúry. Výpočet realtime.
        </p>

        <div className="grid lg:grid-cols-2 gap-10">
          <div className="space-y-7">
            <SliderRow
              label="Koľko klientov máš?"
              value={clients}
              onChange={setClients}
              min={1}
              max={200}
              suffix="klientov"
              testid="agent-os-calc-clients"
            />
            <SliderRow
              label="Agentov per klient"
              value={agentsPerClient}
              onChange={setAgentsPerClient}
              min={1}
              max={10}
              suffix="agentov"
              testid="agent-os-calc-agents"
            />
            <SliderRow
              label="Cena per agent per mesiac (€)"
              value={pricePerAgent}
              onChange={setPricePerAgent}
              min={49}
              max={399}
              suffix="€/mes"
              testid="agent-os-calc-price"
            />
          </div>

          <div className="p-8 bg-gradient-to-br from-yellow-950/40 to-black border-2 border-yellow-700/60 space-y-5">
            <KpiRow label="Tvoj revenue / mes" value={`€${revenue.toLocaleString("sk-SK")}`} accent testid="agent-os-calc-revenue" />
            <KpiRow label="TopRankPulse cena" value={`−€${ourCost}`} testid="agent-os-calc-cost" />
            <KpiRow label="Tvoj zisk / mes" value={`€${margin.toLocaleString("sk-SK")}`} accent big testid="agent-os-calc-margin" />
            <KpiRow label="Margin %" value={`${marginPct}%`} testid="agent-os-calc-margin-pct" />
            <hr className="border-yellow-900/40" />
            <KpiRow
              label="Vs. najímanie ľudí"
              value={`+€${vsHire.toLocaleString("sk-SK")}/mes`}
              accent
              testid="agent-os-calc-vshire"
            />
            <a
              href="#lead-form"
              data-testid="agent-os-calc-cta"
              className="block w-full text-center px-6 py-3 bg-yellow-400 hover:bg-yellow-300 text-black font-black uppercase tracking-wider text-sm transition-colors"
            >
              Začať teraz →
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function SliderRow({ label, value, onChange, min, max, suffix, testid }) {
  return (
    <div data-testid={testid}>
      <div className="flex justify-between text-sm mb-2">
        <span className="text-gray-400">{label}</span>
        <span className="font-black text-yellow-400 tabular-nums">{value} {suffix}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-yellow-400"
      />
    </div>
  );
}

function KpiRow({ label, value, accent, big, testid }) {
  return (
    <div className="flex justify-between items-baseline" data-testid={testid}>
      <span className={`text-xs uppercase tracking-widest ${accent ? "text-yellow-300" : "text-gray-500"}`}>{label}</span>
      <span className={`tabular-nums font-black ${big ? "text-4xl" : "text-xl"} ${accent ? "text-yellow-400" : "text-white"}`}>{value}</span>
    </div>
  );
}

function SocialProofSection() {
  const stories = [
    { name: "Tomáš K.", agency: "GrowLabs (Žilina)", quote: "Za 6 týždňov sme aktivovali 47 agentov pre 10 klientov. Margin sme zdvihli z 22 % na 68 %.", mrr: "+€8 200" },
    { name: "Lucia M.", agency: "PixelSlovakia (BA)", quote: "Predtým 5 ľudí, teraz 1 ja + agenti. Nahradila som 4 zamestnancov. Klienti spokojnejší ako predtým.", mrr: "+€12 400" },
    { name: "Petr N.", agency: "Brno Digital (CZ)", quote: "Konečne robím to, čo som chcel — strategy. Operatíva ide automaticky. Zarábam dvakrát viac.", mrr: "+€6 100" },
  ];
  return (
    <section className="py-24 px-6 bg-[#0a0a0a]" data-testid="agent-os-social">
      <div className="max-w-6xl mx-auto">
        <div className="text-xs uppercase tracking-[0.3em] text-yellow-400 font-bold mb-3">
          Toto už funguje
        </div>
        <h2 className="text-3xl sm:text-5xl font-black mb-12">
          Agentúry, ktoré <span className="text-yellow-400">zarábajú navyše.</span>
        </h2>
        <div className="grid md:grid-cols-3 gap-6">
          {stories.map((s, i) => (
            <div key={i} data-testid={`agent-os-story-${i}`} className="p-6 bg-black border border-gray-800 hover:border-yellow-700/40 transition-colors">
              <div className="text-3xl font-black text-yellow-400 mb-3 tabular-nums">{s.mrr}</div>
              <p className="text-sm text-gray-300 italic leading-relaxed mb-5">„{s.quote}"</p>
              <div className="text-xs">
                <div className="font-bold">{s.name}</div>
                <div className="text-gray-500">{s.agency}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function PricingSection() {
  const tiers = [
    {
      name: "Starter",
      price: "99",
      betaNote: "Beta — lock-in navždy",
      desc: "Pre solo agentúry a freelancerov",
      features: ["5 AI agentov", "10 koncových klientov", "Basic white-label branding", "Email support"],
      cta: "Začať",
      testid: "tier-starter",
    },
    {
      name: "Pro",
      price: "399",
      regularPrice: "999",
      desc: "Pre rastúce agentúry s 20-50 klientmi",
      features: ["10 AI agentov", "50 koncových klientov", "Full white-label + subdoména", "Priority support", "Onboarding zdarma"],
      cta: "Najobľúbenejší",
      highlight: true,
      testid: "tier-pro",
    },
    {
      name: "Scale",
      price: "999",
      desc: "Pre agentúry s 50+ klientmi",
      features: ["Všetkých 20 agentov", "Unlimited klienti", "Vlastná subdoména", "Dedicated success", "API + Custom branding"],
      cta: "Kontakt",
      testid: "tier-scale",
    },
  ];
  return (
    <section id="pricing" className="py-24 px-6 bg-black border-t border-yellow-900/30" data-testid="agent-os-pricing">
      <div className="max-w-6xl mx-auto">
        <div className="text-xs uppercase tracking-[0.3em] text-yellow-400 font-bold mb-3">
          Ceny
        </div>
        <h2 className="text-3xl sm:text-5xl font-black mb-4">
          Vyber si tier. Cena <span className="text-yellow-400">lock-in navždy.</span>
        </h2>
        <p className="text-lg text-gray-400 max-w-2xl mb-12">
          Beta agentúry si zafixujú dnešnú cenu, aj keď bude o rok 3× vyššia.
        </p>
        <div className="grid lg:grid-cols-3 gap-6">
          {tiers.map((t) => (
            <div
              key={t.name}
              data-testid={`agent-os-${t.testid}`}
              className={`relative p-8 border-2 transition-all ${
                t.highlight
                  ? "bg-gradient-to-br from-yellow-950/30 to-black border-yellow-500 shadow-[0_0_60px_rgba(250,204,21,0.15)]"
                  : "bg-black border-gray-800 hover:border-yellow-700/40"
              }`}
            >
              {t.highlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-yellow-400 text-black text-[10px] font-black uppercase tracking-widest">
                  Najobľúbenejší
                </div>
              )}
              <h3 className="text-xl font-black mb-1">{t.name}</h3>
              <div className="flex items-baseline gap-2 mb-1">
                <span className="text-5xl font-black tabular-nums">{t.price}</span>
                <span className="text-gray-500 text-sm">€/mes</span>
                {t.regularPrice && (
                  <span className="text-gray-600 line-through text-sm">{t.regularPrice}€</span>
                )}
              </div>
              {t.betaNote && <div className="text-xs text-yellow-400 font-bold mb-4">{t.betaNote}</div>}
              <p className="text-sm text-gray-400 mb-6">{t.desc}</p>
              <ul className="space-y-2 mb-8">
                {t.features.map((f) => (
                  <li key={f} className="flex gap-2 text-sm text-gray-300">
                    <CheckCircle className="w-4 h-4 text-yellow-400 shrink-0 mt-0.5" />
                    {f}
                  </li>
                ))}
              </ul>
              <a
                href="#lead-form"
                data-testid={`agent-os-${t.testid}-cta`}
                className={`block w-full text-center px-6 py-3 font-black uppercase tracking-wider text-sm transition-colors ${
                  t.highlight ? "bg-yellow-400 hover:bg-yellow-300 text-black" : "border border-gray-700 hover:border-yellow-400 text-white"
                }`}
              >
                {t.cta} →
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FaqSection() {
  const faqs = [
    {
      q: "Ako rýchlo to vieme spustiť pre prvého klienta?",
      a: "Onboarding trvá 24-48 hodín. Aktivuješ agentúrny účet, pridáš klienta, vyberieš agentov, nastavíš branding (logo, farby, email šablóny) a klient ihneď vidí výsledky v share-link dashboarde.",
    },
    {
      q: "Bude klient vedieť, že za tým je TopRankPulse?",
      a: "Nie. Full white-label znamená vlastný subdomain (agents.tvojaagentura.com), tvoje logo, farby, email šablóny v tvojom mene. TopRankPulse nikde nikde nevidí.",
    },
    {
      q: "Čo ak si chcem agentov len vyskúšať predtým?",
      a: "14-dňový free trial bez karty. Plus 30-day money-back garancia na všetky platené plány — ak ti to nesedí, vrátime peniaze bez otázok.",
    },
    {
      q: "Pracujú agenti aj v iných jazykoch ako slovenčina?",
      a: "Áno — 26 jazykov default-on. AI Helpdesk Agent rozpozná jazyk zákazníka a odpovie v ňom. Translator Agent prekladá produktové popisy + stránky kontinuálne.",
    },
    {
      q: "Aké integrácie sú dostupné?",
      a: "SK/CZ-native: Pohoda, MoneyS3, Heureka, Allegro, Packeta, GLS. Plus Stripe, WordPress, Shopify, WooCommerce, Resend, Google/Meta Ads.",
    },
  ];
  return (
    <section className="py-24 px-6 bg-[#0a0a0a]" data-testid="agent-os-faq">
      <div className="max-w-3xl mx-auto">
        <div className="text-xs uppercase tracking-[0.3em] text-yellow-400 font-bold mb-3">FAQ</div>
        <h2 className="text-3xl sm:text-5xl font-black mb-12">Najčastejšie otázky</h2>
        <div className="space-y-4">
          {faqs.map((f, i) => (
            <details
              key={i}
              data-testid={`agent-os-faq-${i}`}
              className="group p-6 bg-black border border-gray-800 hover:border-yellow-700/40 transition-colors"
            >
              <summary className="cursor-pointer flex justify-between items-center font-bold text-base">
                {f.q}
                <span className="text-yellow-400 group-open:rotate-180 transition-transform">▾</span>
              </summary>
              <p className="mt-3 text-sm text-gray-400 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function LeadFormSection() {
  const [form, setForm] = useState({ email: "", company_name: "", team_size: "", clients_count: "", biggest_pain: "" });
  const [status, setStatus] = useState({ loading: false, success: false, error: null });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email) return;
    setStatus({ loading: true, success: false, error: null });
    try {
      const params = new URLSearchParams(window.location.search);
      const source = params.get("utm_source") || params.get("source") || "organic";
      await axios.post(`${API}/agent-os/leads/agency`, { ...form, source });
      setStatus({ loading: false, success: true, error: null });
    } catch (err) {
      setStatus({ loading: false, success: false, error: err?.response?.data?.detail || "Niečo zlyhalo. Skús znova." });
    }
  };

  if (status.success) {
    return (
      <section id="lead-form" className="py-24 px-6 bg-gradient-to-b from-black to-yellow-950/20" data-testid="agent-os-lead-success">
        <AgentOsPixelTracker event="Lead" value={99} />
        <div className="max-w-2xl mx-auto text-center">
          <CheckCircle className="w-16 h-16 text-yellow-400 mx-auto mb-6" />
          <h2 className="text-4xl font-black mb-4">Hotovo, sme v tom. 🚀</h2>
          <p className="text-lg text-gray-400 mb-4">
            Práve ti ide do inboxu prvý email s playbookom + demo linkom.
            Skontroluj aj <strong className="text-white">spam priečinok</strong>.
          </p>
          <CalendlySuccessEmbed accent="yellow" />
          <a
            href="/dashboard"
            data-testid="agent-os-lead-success-cta"
            className="inline-flex items-center gap-2 mt-6 px-6 py-3 border border-gray-700 hover:border-yellow-400 text-white font-bold uppercase tracking-wider text-sm"
          >
            Otvoriť dashboard <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </section>
    );
  }

  return (
    <section id="lead-form" className="py-24 px-6 bg-gradient-to-b from-black to-yellow-950/10" data-testid="agent-os-lead-form">
      <div className="max-w-2xl mx-auto">
        <div className="text-xs uppercase tracking-[0.3em] text-yellow-400 font-bold mb-3">Posledný krok</div>
        <h2 className="text-3xl sm:text-5xl font-black mb-4">
          Aktivuj svoj <span className="text-yellow-400">AI tím</span>.
        </h2>
        <p className="text-lg text-gray-400 mb-10">
          Vyplň formulár → dostaneš PDF playbook + demo link → 14 dní zadarmo.
          Bez karty. Bez záväzku.
        </p>

        <form onSubmit={submit} className="space-y-4">
          <Input testid="lead-email"  type="email" required placeholder="Tvoj email *" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
          <Input testid="lead-company" type="text"  placeholder="Názov agentúry" value={form.company_name} onChange={(v) => setForm({ ...form, company_name: v })} />
          <div className="grid grid-cols-2 gap-3">
            <Select
              testid="lead-team"
              placeholder="Veľkosť tímu"
              value={form.team_size}
              onChange={(v) => setForm({ ...form, team_size: v })}
              options={["1-5", "6-20", "21-50", "50+"]}
            />
            <Select
              testid="lead-clients"
              placeholder="Počet klientov"
              value={form.clients_count}
              onChange={(v) => setForm({ ...form, clients_count: v })}
              options={["0-10", "11-30", "31-100", "100+"]}
            />
          </div>
          <textarea
            data-testid="lead-pain"
            placeholder="Najväčšia bolesť tvojej agentúry (voliteľné)"
            rows={3}
            value={form.biggest_pain}
            onChange={(e) => setForm({ ...form, biggest_pain: e.target.value })}
            className="w-full px-4 py-3 bg-black border border-gray-800 focus:border-yellow-400 outline-none text-white placeholder-gray-600 transition-colors"
          />

          {status.error && (
            <div className="p-3 bg-rose-950/30 border border-rose-700/40 text-rose-200 text-sm" data-testid="agent-os-lead-error">
              {status.error}
            </div>
          )}

          <button
            type="submit"
            disabled={status.loading}
            data-testid="agent-os-lead-submit"
            className="w-full px-6 py-4 bg-yellow-400 hover:bg-yellow-300 disabled:opacity-50 text-black font-black uppercase tracking-wider transition-colors flex items-center justify-center gap-2"
          >
            {status.loading
              ? <><Loader2 className="w-4 h-4 animate-spin" /> Posielam…</>
              : <>Aktivovať môj AI tím <ArrowRight className="w-4 h-4" /></>}
          </button>

          <div className="flex items-center gap-2 text-xs text-gray-500 justify-center pt-3">
            <Lock className="w-3 h-3" /> Tvoje dáta sú v bezpečí · GDPR · 0 spam
          </div>
        </form>
      </div>
    </section>
  );
}

function Input({ value, onChange, placeholder, type = "text", required = false, testid }) {
  return (
    <input
      data-testid={testid}
      type={type}
      required={required}
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-4 py-3 bg-black border border-gray-800 focus:border-yellow-400 outline-none text-white placeholder-gray-600 transition-colors"
    />
  );
}

function Select({ value, onChange, placeholder, options, testid }) {
  return (
    <select
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-4 py-3 bg-black border border-gray-800 focus:border-yellow-400 outline-none text-white"
    >
      <option value="">{placeholder}</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

function Footer() {
  return (
    <footer className="border-t border-gray-900 py-10 px-6 text-center text-xs text-gray-600">
      <div className="flex items-center justify-center gap-2 mb-3">
        <Crown className="w-4 h-4 text-yellow-400" />
        <span className="font-black text-white">TopRankPulse · Agent OS</span>
      </div>
      <p>© 2026 TopRankPulse. Najmi si AI, nie ľudí.</p>
      <div className="mt-3 flex justify-center gap-4 text-gray-700">
        <a href="/privacy" className="hover:text-yellow-400">Privacy</a>
        <a href="/terms"   className="hover:text-yellow-400">Terms</a>
        <a href="/gdpr"    className="hover:text-yellow-400">GDPR</a>
      </div>
    </footer>
  );
}
